import { getLocalizedValue } from "@features/drivers/data";
import { isOriginalPriceEnabled } from "@features/products/data";
import {
  DiscountType,
  type HighlightedProductsEntity,
  type ProductEntity,
} from "@features/products/domain";
import * as variantsResolver from "@taager-experience-shared/variants";
import type {
  ListHighlightedVariantsResponseModel,
  ProductVariantModel,
} from "../models/products.models";

export function mapHighlightedProducts(
  highlightedCategories: ListHighlightedVariantsResponseModel,
): HighlightedProductsEntity {
  const highlightProducts: Partial<HighlightedProductsEntity> = {};

  for (const [key, variants] of Object.entries(highlightedCategories)) {
    highlightProducts[key] = variants.map(mapProduct);
  }

  return highlightProducts as HighlightedProductsEntity;
}

export function mapProduct(variant: ProductVariantModel): ProductEntity {
  const {
    discountType,
    discountedPrice,
    price,
    originalPrice,
    discountedProfit,
  } = variant.financials;

  let finalPriceWithoutFavoritePrice: number;

  if (isOriginalPriceEnabled()) {
    if (
      discountType === DiscountType.SALE &&
      discountedPrice &&
      discountedProfit
    ) {
      finalPriceWithoutFavoritePrice = discountedPrice - discountedProfit;
    } else {
      finalPriceWithoutFavoritePrice = originalPrice;
    }
  } else {
    if (discountType === DiscountType.SALE) {
      finalPriceWithoutFavoritePrice = discountedPrice!;
    } else {
      finalPriceWithoutFavoritePrice = price;
    }
  }

  const colorAttribute = variant.attributes?.find(attr => attr.key === "color");
  const sizeAttribute = variant.attributes?.find(attr => attr.key === "size");

  return {
    // TODO: Remove this after product details migration is complete
    newParentId: variant.productId,
    legacyParentId: variant.productLegacyId,
    legacyVariantId: variant.variantLegacyId,

    parentId: variant.productLegacyId || variant.productId,
    variantId: variant.variantLegacyId || variant.variantId,
    isFavorite: variant.isFavourite,
    sku: variant.sku,
    name: variant.name,
    type: variant.type,
    thumbnail: variant.thumbnail,
    financials: {
      price: variant.financials.price,
      originalPrice: variant.financials.originalPrice,
      profit: variant.financials.profit,
      favouritePrice: variant.financials.favouritePrice,
      ...(variant.financials.discountType && {
        discount: {
          price: variant.financials.discountedPrice,
          type: variant.financials.discountType,
          originalPrice: isOriginalPriceEnabled()
            ? variant.financials.discountedPrice! -
              variant.financials.discountedProfit!
            : variant.financials.discountedPrice!,
          ...(variant.financials.discountStartsAt && {
            startsAt: new Date(variant.financials.discountStartsAt),
          }),
          ...(variant.financials.discountExpiresAt && {
            expiresAt: new Date(variant.financials.discountExpiresAt),
          }),
        },
      }),
      finalPrice:
        variant.financials.favouritePrice || finalPriceWithoutFavoritePrice,
      pricingRange: {
        minPrice:
          variant.financials.pricingRange?.min ||
          finalPriceWithoutFavoritePrice,
        maxPrice: variant.financials.pricingRange?.max,
      },
      tieredPricing: Array.isArray(variant.financials?.tieredPricing)
        ? variant.financials.tieredPricing.map(tier => ({
            quantity: tier.quantity,
            price: tier.price,
            profit: tier.profit,
            dynamicPrice: tier.dynamicPrice,
            dynamicProfit: tier.dynamicProfit,
          }))
        : undefined,
      discountedPrice: variant.financials.discountedPrice,
      discountedProfit: variant.financials.discountedProfit,
    },
    orderTestingInfo: variant.orderTestingInfo,
    lockInfo: {
      isLocked: variant.lockInfo.isLocked,
      isOpenForTesting: variant.lockInfo.isOpenForTesting,
      quantity: variant.lockInfo.quantity,
      quantityUsed: variant.lockInfo.quantityUsed,
      ...(variant.lockInfo.expiresAt && {
        expiresAt: new Date(variant.lockInfo.expiresAt),
      }),
    },
    stockAvailability: variant.stockAvailability,
    isMultiVariant: variant.isMultiVariant,
    isPrimary: variant.isPrimary,
    isOnSale: variant.financials.discountType === DiscountType.SALE,
    hasSecondPlacementDiscount:
      variant.financials.discountType === DiscountType.SECOND_PLACEMENT,
    hasAdditionalQuantityDiscount:
      variant.financials.discountType === DiscountType.ADDITIONAL_QUANTITY,
    color: colorAttribute?.value
      ? {
          name: getLocalizedValue(
            variantsResolver.getColorByHexCode(colorAttribute.value),
          ),
          hex: colorAttribute.value,
        }
      : undefined,
    size: sizeAttribute?.value,
  };
}
